
import React, { useState } from 'react';
import { useCountry } from '@/contexts/country'; // Updated import path
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CountryFlag from './CountryFlag';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type CountryFilterType = 'active' | 'coming-soon' | 'all';

const CountryList = () => {
  const { availableCountries, setCountry } = useCountry();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CountryFilterType>('active');

  const handleSelectCountry = (code: string) => {
    setCountry(code);
    // Navigate to the country insights page instead of homepage
    navigate(`/insights/${code}`);
  };

  const filteredCountries = availableCountries.filter(country => {
    if (filter === 'active') return country.active;
    if (filter === 'coming-soon') return !country.active;
    return true; // 'all' filter
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <div className="w-48">
          <Select 
            value={filter} 
            onValueChange={(value) => setFilter(value as CountryFilterType)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Filter countries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active Countries</SelectItem>
              <SelectItem value="coming-soon">Coming Soon</SelectItem>
              <SelectItem value="all">All Countries</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCountries.map(country => (
          <Card key={country.code} className={country.active ? 'border-nation-teal' : 'border-gray-200'}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <CountryFlag countryCode={country.code} className="h-5 w-6" />
                  <CardTitle className="text-lg font-semibold text-nation-blue">
                    {country.name}
                  </CardTitle>
                </div>
                {country.active ? (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                    <Check size={12} className="mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full flex items-center">
                    <Clock size={12} className="mr-1" />
                    Coming Soon
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {country.active ? (
                <Button 
                  variant="default" 
                  className="w-full bg-nation-teal hover:bg-nation-teal-dark"
                  onClick={() => handleSelectCountry(country.code)}
                >
                  View {country.name}
                </Button>
              ) : (
                <Button variant="outline" className="w-full" disabled>
                  Not Available Yet
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CountryList;
